import { useState, useEffect, useRef } from "react";
import { apiRequest } from "src/services/apiClient";
import { sanitizeInput, trimInput, normalizePlz } from "src/helpers/sanitizeInput";
import MultiPhoneInput from "src/components/PhoneInput/MultiPhoneInput";
import { useTranslation } from "react-i18next";
import { birthDateBounds, isBirthDateValid } from "src/helpers/birthDateBounds";
import { TbUser, TbUsers, TbQrcode, TbPencil, TbPrinter } from "react-icons/tb";
import { config } from "configs/config";
import "./createRecipientModal.scss";
import { CityInput } from "src/components/CityInput/CityInput";

type Mode = "pick" | "manual" | "qr";
type PatientChoice = "self" | "other";

interface Props {
    onClose: () => void;
    onCreated: () => void;
}

export default function CreateRecipientModal({ onClose, onCreated }: Props) {
    const { t } = useTranslation();

    const SALUTATION_OPTIONS = [
        { value: "H", label: t("form.herr") },
        { value: "F", label: t("form.frau") },
    ];

    // ── Recipient (billing contact) fields ──────────────────────────────────────
    const [salutation, setSalutation] = useState("H");
    const [title, setTitle]           = useState("");
    const [firstName, setFirstName]   = useState("");
    const [lastName, setLastName]     = useState("");
    const [birthDate, setBirthDate]   = useState("");
    const [email, setEmail]           = useState("");
    const [tel, setTel]               = useState<string[]>([]);
    const [street, setStreet]         = useState("");
    const [plz, setPlz]               = useState("");
    const [city, setCity]             = useState("");

    // ── Patient fields (step 2, "other" only) ──────────────────────────────────
    const [newPatSalutation, setNewPatSalutation] = useState("H");
    const [newPatTitle,      setNewPatTitle]      = useState("");
    const [newPatFirstName,  setNewPatFirstName]  = useState("");
    const [newPatLastName,   setNewPatLastName]   = useState("");
    const [newPatBirthDate,  setNewPatBirthDate]  = useState("");
    const [newPatEmail,      setNewPatEmail]      = useState("");
    const [newPatTel,        setNewPatTel]        = useState<string[]>([]);
    const [newPatStreet,     setNewPatStreet]     = useState("");
    const [newPatPlz,        setNewPatPlz]        = useState("");
    const [newPatCity,       setNewPatCity]       = useState("");

    // ── Wizard state ────────────────────────────────────────────────────────────
    const [mode, setMode]                       = useState<Mode>("pick");
    const [step, setStep]                       = useState<1 | 2>(1);
    const [patientChoice, setPatientChoice]     = useState<PatientChoice>("self");

    const [saving, setSaving]   = useState(false);
    const [error, setError]     = useState<string | null>(null);

    // ── QR state ────────────────────────────────────────────────────────────────
    const [qrLoading, setQrLoading] = useState(false);
    const [qrUrl, setQrUrl]         = useState<string | null>(null);
    const [qrHash, setQrHash]       = useState<string | null>(null);
    const [qrError, setQrError]     = useState<string | null>(null);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const stopPolling = () => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };

    useEffect(() => () => stopPolling(), []);

    const startPolling = (hash: string) => {
        stopPolling();
        pollRef.current = setInterval(async () => {
            try {
                const token    = localStorage.getItem("token");
                const praxisId = JSON.parse(localStorage.getItem("praxis") || "null")?.id;
                const doctorId = JSON.parse(localStorage.getItem("doctor") || "null")?.id;
                const res = await fetch(
                    `${config.baseUrl}/api/patient-portal/questionnaire/blank-status?h=${hash}`,
                    {
                        headers: {
                            ...(token    && { Authorization: `Bearer ${token}` }),
                            ...(praxisId && { "X-Praxis-Id": praxisId }),
                            ...(doctorId && { "X-Doctor-Id": doctorId }),
                        },
                    },
                );
                if (!res.ok) return;
                const data = await res.json();
                if (data.used) {
                    stopPolling();
                    onCreated();
                    onClose();
                }
            } catch { /* ignore transient errors */ }
        }, 2500);
    };

    const loadBlankQr = async () => {
        setQrLoading(true);
        setQrError(null);
        try {
            const token    = localStorage.getItem("token");
            const praxisId = JSON.parse(localStorage.getItem("praxis") || "null")?.id;
            const doctorId = JSON.parse(localStorage.getItem("doctor") || "null")?.id;
            const res = await fetch(`${config.baseUrl}/api/patient-portal/questionnaire/create-blank-qr`, {
                method: "POST",
                headers: {
                    ...(token    && { Authorization: `Bearer ${token}` }),
                    ...(praxisId && { "X-Praxis-Id": praxisId }),
                    ...(doctorId && { "X-Doctor-Id": doctorId }),
                },
            });
            if (!res.ok) throw new Error("QR generation failed");
            const data = await res.json();
            setQrUrl(data.qrDataUrl);
            setQrHash(data.hash);
            startPolling(data.hash);
        } catch {
            setQrError(t("createRecipient.qrError"));
        } finally {
            setQrLoading(false);
        }
    };

    // Opens the QR in its own tab so staff can print it (e.g. to hand a paper copy to a
    // patient waiting without a smartphone in hand). qrUrl is a data: URI (see
    // create-blank-qr's response), and Chrome refuses to navigate a new tab straight to a
    // data: URL (window.open(qrUrl, "_blank") silently lands on a blank tab) — unlike the
    // blob: URL ConsentModal.tsx opens for its PDF preview, which isn't restricted this way.
    // Writing the image into a blank window's own document sidesteps that, and since the
    // button is explicitly "print", triggers the print dialog once the image has loaded.
    const printQr = () => {
        if (!qrUrl) return;
        const win = window.open("", "_blank");
        if (!win) return;
        win.document.write(`<!DOCTYPE html><html><head><title>QR-Code</title></head>` +
            `<body style="margin:0;display:flex;align-items:center;justify-content:center;height:100vh;">` +
            `<img src="${qrUrl}" style="width:320px;height:320px;" onload="window.print()" />` +
            `</body></html>`);
        win.document.close();
    };

    const validateStep1 = (): boolean => {
        if (!lastName.trim()) { setError(t("createRecipient.validationError")); return false; }
        if (birthDate && !isBirthDateValid(birthDate)) { setError(t("form.birthDateInvalid")); return false; }
        return true;
    };

    const doSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const praxisId   = JSON.parse(localStorage.getItem("praxis") || "null")?.id ?? "";
            const doctorId   = JSON.parse(localStorage.getItem("doctor") || "null")?.id ?? "";
            const recipientId = crypto.randomUUID();

            await apiRequest(
                "/recipients",
                { method: "POST", body: JSON.stringify({
                    id: recipientId,
                    praxisId,
                    doctorId,
                    salutation,
                    title:     title     || undefined,
                    firstName: firstName || undefined,
                    lastName,
                    birthDate: birthDate || undefined,
                    email:     email     || undefined,
                    tel:       tel.filter(Boolean),
                    street:    street    || undefined,
                    plz:       plz       || undefined,
                    city:      city      || undefined,
                }) },
                { "Content-Type": "application/json" },
            );

            if (patientChoice === "self") {
                await apiRequest(
                    "/patients",
                    { method: "POST", body: JSON.stringify({
                        id: crypto.randomUUID(),
                        salutation,
                        title:     title     || undefined,
                        firstName: firstName || undefined,
                        lastName,
                        birthDate: birthDate || undefined,
                        email:     email     || undefined,
                        tel:       tel.filter(Boolean),
                        street:    street    || undefined,
                        plz:       plz       || undefined,
                        city:      city      || undefined,
                        recipientId,
                    }) },
                    { "Content-Type": "application/json" },
                );
            } else {
                await apiRequest(
                    "/patients",
                    { method: "POST", body: JSON.stringify({
                        id: crypto.randomUUID(),
                        salutation: newPatSalutation,
                        title:     newPatTitle     || undefined,
                        firstName: newPatFirstName || undefined,
                        lastName:  newPatLastName,
                        birthDate: newPatBirthDate || undefined,
                        email:     newPatEmail     || undefined,
                        tel:       newPatTel.filter(Boolean),
                        street:    newPatStreet    || undefined,
                        plz:       newPatPlz       || undefined,
                        city:      newPatCity      || undefined,
                        recipientId,
                    }) },
                    { "Content-Type": "application/json" },
                );
            }

            onCreated();
            onClose();
        } catch {
            setError(t("createRecipient.createError"));
        } finally {
            setSaving(false);
        }
    };

    const handleStep1Submit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (!validateStep1()) return;
        if (patientChoice === "other") {
            setStep(2);
        } else {
            doSave();
        }
    };

    const handleStep2Submit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (!newPatLastName.trim()) { setError(t("createRecipient.patientNewValidation")); return; }
        doSave();
    };

    const recipientName = [title, firstName, lastName].filter(Boolean).join(" ");

    return (
        <div className="create-modal-overlay" onClick={onClose}>
            <div className="create-modal" onClick={(e) => e.stopPropagation()}>

                {/* ── Step indicator (only in manual flow) ── */}
                {mode === "manual" && (
                    <div className="create-modal__steps">
                        <div className={`create-modal__step${step === 1 ? " create-modal__step--active" : ""}`}>
                            <span className="create-modal__step-num">1</span>
                            <span className="create-modal__step-label">{t("createRecipient.stepRecipient")}</span>
                        </div>
                        <div className="create-modal__step-line" />
                        <div className={`create-modal__step${step === 2 ? " create-modal__step--active" : ""}`}>
                            <span className="create-modal__step-num">2</span>
                            <span className="create-modal__step-label">{t("createRecipient.stepPatient")}</span>
                        </div>
                    </div>
                )}

                <h2 className="create-modal__title">
                    {mode === "pick"
                        ? t("createRecipient.title")
                        : mode === "qr"
                        ? t("createRecipient.qrTitle")
                        : step === 2
                        ? t("createRecipient.patientTitle")
                        : t("createRecipient.title")}
                </h2>

                {/* ══════════════ MODE PICKER ══════════════ */}
                {mode === "pick" && (
                    <div className="create-modal__mode-pick">
                        <button
                            type="button"
                            className="create-modal__mode-opt"
                            onClick={() => { setMode("qr"); loadBlankQr(); }}
                        >
                            <span className="create-modal__mode-opt-icon create-modal__mode-opt-icon--qr">
                                <TbQrcode />
                            </span>
                            <span className="create-modal__mode-opt-text">
                                <strong>{t("createRecipient.modeQr")}</strong>
                                <small>{t("createRecipient.modeQrDesc")}</small>
                            </span>
                        </button>
                        <button
                            type="button"
                            className="create-modal__mode-opt"
                            onClick={() => setMode("manual")}
                        >
                            <span className="create-modal__mode-opt-icon create-modal__mode-opt-icon--manual">
                                <TbPencil />
                            </span>
                            <span className="create-modal__mode-opt-text">
                                <strong>{t("createRecipient.modeManual")}</strong>
                                <small>{t("createRecipient.modeManualDesc")}</small>
                            </span>
                        </button>
                        <div className="create-modal__actions">
                            <button type="button" className="create-modal__btn create-modal__btn--cancel" onClick={onClose}>
                                {t("form.cancel")}
                            </button>
                        </div>
                    </div>
                )}

                {/* ══════════════ QR SCREEN ══════════════ */}
                {mode === "qr" && (
                    <div className="create-modal__qr-screen">
                        <p className="create-modal__lead">{t("createRecipient.qrHint")}</p>
                        {qrLoading && <div className="create-modal__qr-loading"><span className="skeleton" style={{ width: 240, height: 240, borderRadius: 8 } as any} /></div>}
                        {qrUrl && <img className="create-modal__qr-img" src={qrUrl} alt="QR Code" />}
                        {qrError && <p className="create-modal__error">{qrError}</p>}
                        <div className="create-modal__actions">
                            <button type="button" className="create-modal__btn create-modal__btn--cancel" onClick={() => { stopPolling(); setMode("pick"); setQrUrl(null); setQrHash(null); }}>
                                ← {t("createRecipient.back")}
                            </button>
                            {qrUrl && (
                                <button type="button" className="create-modal__btn create-modal__btn--ghost" onClick={printQr}>
                                    <TbPrinter /> {t("createRecipient.qrPrint")}
                                </button>
                            )}
                            <button type="button" className="create-modal__btn create-modal__btn--submit" onClick={() => { stopPolling(); onClose(); }}>
                                {t("popup.close")}
                            </button>
                        </div>
                    </div>
                )}

                {/* ══════════════ STEP 1 ══════════════ */}
                {mode === "manual" && step === 1 && (
                    <form className="create-modal__form" onSubmit={handleStep1Submit}>
                        <div className="create-modal__row">
                            <div className="create-modal__field">
                                <label htmlFor="cr-salutation">{t("form.salutation")}</label>
                                <select id="cr-salutation" autoFocus value={salutation} onChange={(e) => setSalutation(e.target.value)}>
                                    {SALUTATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                            <div className="create-modal__field">
                                <label htmlFor="cr-title">{t("form.title")}</label>
                                <input id="cr-title" value={title} maxLength={20} placeholder={t("form.drPlaceholder")} onChange={(e) => setTitle(sanitizeInput(e.target.value))} onBlur={(e) => setTitle(trimInput(e.target.value))} />
                            </div>
                        </div>

                        <div className="create-modal__row">
                            <div className="create-modal__field">
                                <label htmlFor="cr-firstname">{t("form.firstName")}</label>
                                <input id="cr-firstname" value={firstName} maxLength={50} onChange={(e) => setFirstName(sanitizeInput(e.target.value))} onBlur={(e) => setFirstName(trimInput(e.target.value))} />
                            </div>
                            <div className="create-modal__field">
                                <label htmlFor="cr-lastname">{t("form.lastName")} *</label>
                                <input id="cr-lastname" value={lastName} maxLength={50} onChange={(e) => setLastName(sanitizeInput(e.target.value))} onBlur={(e) => setLastName(trimInput(e.target.value))} />
                            </div>
                        </div>

                        <div className="create-modal__row">
                            <div className="create-modal__field">
                                <label htmlFor="cr-birthdate">{t("form.birthDate")}</label>
                                <input id="cr-birthdate" type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} {...birthDateBounds()} />
                            </div>
                            <div className="create-modal__field">
                                <label htmlFor="cr-email">{t("form.email")}</label>
                                <input id="cr-email" type="email" value={email} onChange={(e) => setEmail(sanitizeInput(e.target.value))} onBlur={(e) => setEmail(trimInput(e.target.value))} />
                            </div>
                        </div>

                        <div className="create-modal__row">
                            <div className="create-modal__field">
                                <label>{t("form.phone")}</label>
                                <MultiPhoneInput value={tel} onChange={setTel} />
                            </div>
                            <div className="create-modal__field">
                                <label htmlFor="cr-street">{t("form.street")}</label>
                                <input id="cr-street" value={street} onChange={(e) => setStreet(sanitizeInput(e.target.value))} onBlur={(e) => setStreet(trimInput(e.target.value))} />
                            </div>
                        </div>

                        <div className="create-modal__row">
                            <div className="create-modal__field create-modal__field--short">
                                <label htmlFor="cr-plz">{t("form.plz")}</label>
                                <input id="cr-plz" value={plz} onChange={(e) => setPlz(normalizePlz(e.target.value))} />
                            </div>
                            <div className="create-modal__field">
                                <label htmlFor="cr-city">{t("form.city")}</label>
                                <CityInput id="cr-city" plz={plz} value={city} onChange={(v) => setCity(sanitizeInput(v))} onBlur={(v) => setCity(trimInput(v))} />
                            </div>
                        </div>

                        {/* ── Patient question ── */}
                        <div className="create-modal__section-divider">
                            <span>{t("createRecipient.patientQuestion")}</span>
                        </div>
                        <div className="create-modal__billing-options">
                            <button
                                type="button"
                                className={`create-modal__billing-opt${patientChoice === "self" ? " create-modal__billing-opt--active" : ""}`}
                                onClick={() => setPatientChoice("self")}
                            >
                                <span className="create-modal__billing-opt-icon"><TbUser /></span>
                                <span className="create-modal__billing-opt-text">
                                    <strong>{t("createRecipient.patientOptionSelf")}</strong>
                                    <small>{t("createRecipient.patientOptionSelfDesc")}</small>
                                </span>
                            </button>
                            <button
                                type="button"
                                className={`create-modal__billing-opt${patientChoice === "other" ? " create-modal__billing-opt--active" : ""}`}
                                onClick={() => setPatientChoice("other")}
                            >
                                <span className="create-modal__billing-opt-icon"><TbUsers /></span>
                                <span className="create-modal__billing-opt-text">
                                    <strong>{t("createRecipient.patientOptionOther")}</strong>
                                    <small>{t("createRecipient.patientOptionOtherDesc")}</small>
                                </span>
                            </button>
                        </div>

                        {error && <p className="create-modal__error">{error}</p>}

                        <div className="create-modal__actions">
                            <button type="button" className="create-modal__btn create-modal__btn--cancel" onClick={onClose}>
                                {t("form.cancel")}
                            </button>
                            <button type="submit" className="create-modal__btn create-modal__btn--submit" disabled={saving}>
                                {saving
                                    ? t("createRecipient.creating")
                                    : patientChoice === "other"
                                        ? t("createRecipient.next")
                                        : t("createRecipient.create")}
                            </button>
                        </div>
                    </form>
                )}

                {/* ══════════════ STEP 2 ══════════════ */}
                {mode === "manual" && step === 2 && (
                    <form className="create-modal__form" onSubmit={handleStep2Submit}>
                        {recipientName && (
                            <p className="create-modal__step-context">
                                {t("createRecipient.recipientContext", { name: recipientName })}
                            </p>
                        )}

                        <div className="create-modal__row">
                            <div className="create-modal__field">
                                <label>{t("form.salutation")}</label>
                                <select autoFocus value={newPatSalutation} onChange={(e) => setNewPatSalutation(e.target.value)}>
                                    {SALUTATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                            <div className="create-modal__field">
                                <label>{t("form.title")}</label>
                                <input value={newPatTitle} maxLength={20} placeholder={t("form.drPlaceholder")} onChange={(e) => setNewPatTitle(sanitizeInput(e.target.value))} onBlur={(e) => setNewPatTitle(trimInput(e.target.value))} />
                            </div>
                        </div>

                        <div className="create-modal__row">
                            <div className="create-modal__field">
                                <label>{t("form.firstName")}</label>
                                <input value={newPatFirstName} maxLength={50} onChange={(e) => setNewPatFirstName(sanitizeInput(e.target.value))} onBlur={(e) => setNewPatFirstName(trimInput(e.target.value))} />
                            </div>
                            <div className="create-modal__field">
                                <label>{t("form.lastName")} *</label>
                                <input value={newPatLastName} maxLength={50} onChange={(e) => setNewPatLastName(sanitizeInput(e.target.value))} onBlur={(e) => setNewPatLastName(trimInput(e.target.value))} />
                            </div>
                        </div>

                        <div className="create-modal__row">
                            <div className="create-modal__field">
                                <label>{t("form.birthDate")}</label>
                                <input type="date" value={newPatBirthDate} onChange={(e) => setNewPatBirthDate(e.target.value)} {...birthDateBounds()} />
                            </div>
                            <div className="create-modal__field">
                                <label>{t("form.email")}</label>
                                <input type="email" value={newPatEmail} onChange={(e) => setNewPatEmail(sanitizeInput(e.target.value))} onBlur={(e) => setNewPatEmail(trimInput(e.target.value))} />
                            </div>
                        </div>

                        <div className="create-modal__row">
                            <div className="create-modal__field">
                                <label>{t("form.phone")}</label>
                                <MultiPhoneInput value={newPatTel} onChange={setNewPatTel} />
                            </div>
                            <div className="create-modal__field">
                                <label>{t("form.street")}</label>
                                <input value={newPatStreet} onChange={(e) => setNewPatStreet(sanitizeInput(e.target.value))} onBlur={(e) => setNewPatStreet(trimInput(e.target.value))} />
                            </div>
                        </div>

                        <div className="create-modal__row">
                            <div className="create-modal__field create-modal__field--short">
                                <label>{t("form.plz")}</label>
                                <input value={newPatPlz} onChange={(e) => setNewPatPlz(normalizePlz(e.target.value))} />
                            </div>
                            <div className="create-modal__field">
                                <label>{t("form.city")}</label>
                                <CityInput plz={newPatPlz} value={newPatCity} onChange={(v) => setNewPatCity(sanitizeInput(v))} onBlur={(v) => setNewPatCity(trimInput(v))} />
                            </div>
                        </div>

                        {error && <p className="create-modal__error">{error}</p>}

                        <div className="create-modal__actions">
                            <button
                                type="button"
                                className="create-modal__btn create-modal__btn--cancel create-modal__btn--back"
                                onClick={() => { setStep(1); setError(null); }}
                            >
                                ← {t("createRecipient.back")}
                            </button>
                            <button type="submit" className="create-modal__btn create-modal__btn--submit" disabled={saving}>
                                {saving ? t("createRecipient.creating") : t("createRecipient.create")}
                            </button>
                        </div>
                    </form>
                )}

            </div>
        </div>
    );
}
